import { Annotation } from "../../annotator/interfaces/annotation";
import { useMemo } from "react";
import { useAppSelector } from "../../app";
import { Status } from "../../common/status/status";
import { shallowEqual } from "react-redux";

const ATTENTION_THRESHOLD = 90;

const STATUS_LIST = [
  Status.ToDo,
  Status.InProgress,
  Status.OnHold,
  Status.ExportFailed,
  Status.Error,
];

const useEntityAttention = (annotations: Array<Annotation>) => {
  const { activeDocumentSet } = useAppSelector(
    (state) => state.documentSetsReducer,
    shallowEqual,
  );

  const settings = useAppSelector(
    (state) => state.userReducer.user?.organization.settings,
    shallowEqual,
  );

  const requiresScoreAttention = useMemo(() => {
    if (settings?.showConfidenceScores === false) {
      return false;
    }

    if (!activeDocumentSet || !STATUS_LIST.includes(activeDocumentSet.status)) {
      return false;
    }

    const total = annotations
      .map((a) => {
        if (!a.modelScore) {
          return 100;
        }

        return a.modelScore;
      })
      .reduce((acc, curr) => acc + curr, 0);

    const avg = total / annotations.length;

    return (
      avg <= (settings?.confidenceAttentionThreshold || ATTENTION_THRESHOLD)
    );
  }, [
    settings?.showConfidenceScores,
    settings?.confidenceAttentionThreshold,
    activeDocumentSet,
    annotations,
  ]);

  const requiresErrorAttention = useMemo(() => {
    if (!activeDocumentSet || !STATUS_LIST.includes(activeDocumentSet.status)) {
      return false;
    }

    return annotations
      .map((a) => {
        return (
          a.entityAnnotationNormalization?.normalizedValue?.toLowerCase() ===
          "invalid value"
        );
      })
      .includes(true);
  }, [annotations, activeDocumentSet]);

  return { requiresScoreAttention, requiresErrorAttention };
};

export default useEntityAttention;
